import React, { useEffect, useMemo, useRef, useState } from "react";
import Konva from "konva";
import { KonvaEventObject } from "konva/types/Node";
import { Group, Shape } from "react-konva";
import { Portal } from "react-konva-utils";
import consts from "shared/consts";
import { LockType, Point, Connector, defaultShapeDimensions } from "shared/datamodel/schemas";
import { getTransformParams } from "frontend/utils/node-utils";
import { isPointInRect, viewportToStage } from "frontend/utils/math-utils";
import { ITraits, Trait } from "../../elements-toolbar/elements-toolbar-types";
import { useAtomValue, useSetAtom } from "jotai";
import { connectorTransformingAtom, isExportingAtom } from "state-atoms";
import { getElementTypeForId } from "../canvas-elements-utils";
import { getFontSize } from "../../text-element";
import { useDeepEqualMemo } from "frontend/hooks/use-deep-memoize";
import { ITransformHooks } from "frontend/hooks/use-transform-hooks";
import { ShapeSocketColor } from "../../frontend-consts";
import Transform, { Degrees } from "frontend/utils/transform";
import { noop } from "frontend/utils/fn-utils";
import { produce, enablePatches } from "immer";
import { ConnectorEndpoint } from "./connector-utils";
import * as utils from "./connector-utils";
import * as anchorUtils from "./anchor-point-utils";
import * as attachUtils from "./connector-attach-utils";
import * as connectorConsts from "./connector-constants";
import pointAt from "frontend/geometry/pointAt";
import normalAt from "frontend/geometry/normalAt";
import { ConnectorLineAndText } from "./connector-line";
import { computeConnectorDrawingData, drawScaledCircle } from "./connector-drawing-utils";
import useConnectorShapes from "frontend/hooks/use-connector-shapes";
import { parseStrokeWidth } from "shared/util/utils";
import { ConnectorEditor } from "./connector-editor";
import { AnchorPoints } from "./anchor-points";
import { useTrackRenderCountForTests } from "utils/tests/use-track-render-count-for-tests";
import { useEvent } from "react-use";
import { EVT_ELEMENT_DRAG_START, EVT_ELEMENT_DROP } from "../card-stack/card-stack-utils";

enablePatches();

export const ConnectorCanvasElement = React.memo(
  ({
    uniqueId,
    element,
    mutation,
    isSelected,
    isEditing,
    layer,
    onElementsMutationEnded,
    changeElement,
    changeAnyElement,
    isSelectable,
    allElementsData,
    selectedIdsRef,
  }: {
    uniqueId: string;
    element: Connector;
    mutation: ITransformHooks;
    isSelected: boolean;
    isEditing: boolean;
    layer?: Konva.Layer | null;
    onElementsMutationEnded: any;
    changeElement: utils.OnChangeElementFn;
    changeAnyElement: (id: string, props: any, previousProps?: any) => void;
    isSelectable: boolean;
    allElementsData?: Array<[string, any]>;
    selectedIdsRef?: React.MutableRefObject<string[]>;
  }) => {
    useTrackRenderCountForTests(uniqueId);
    const { startShape, endShape, currentElement, getShape } = useConnectorShapes(uniqueId, element, allElementsData);

    // Atoms
    const isExportingCanvas = useAtomValue(isExportingAtom);
    const setConnectorTransforming = useSetAtom(connectorTransformingAtom);
    // Hooks
    const anchorsFrameIds = useRef<[utils.AnchorFrameData, utils.AnchorFrameData]>([null, null]);
    const [dragState, setDragState] = useState<any>(null);
    const undoState = useRef<any>();
    const connectedShapes = element.connectedShapes;
    const locked = (element.lock ?? 0) != LockType.None;

    // Only show anchors when this connector is the ONLY selected element
    const isOnlySelectedConnector = useMemo(() => {
      if (!selectedIdsRef?.current) return false;
      return selectedIdsRef.current.length === 1 && selectedIdsRef.current[0] === uniqueId;
    }, [selectedIdsRef?.current, uniqueId]);

    const drawHandles = isOnlySelectedConnector && !locked && !isExportingCanvas;
    const [isDragging, setIsDragging] = useState(false);
    const [isKeyboardMoving, setIsKeyboardMoving] = useState(false);

    // Reset dragging state when selection changes
    useEffect(() => {
      if (isSelected) {
        setIsDragging(false);
      }
    }, [isSelected]);

    // Add a fallback reset on mouse up anywhere
    useEffect(() => {
      const handleGlobalMouseUp = () => {
        setIsDragging(false);
      };

      if (isDragging) {
        document.addEventListener("mouseup", handleGlobalMouseUp);
        return () => document.removeEventListener("mouseup", handleGlobalMouseUp);
      }
    }, [isDragging]);

    // Keep the existing event listeners but don't override mutation callbacks
    useEvent(EVT_ELEMENT_DRAG_START, (event: CustomEvent<{ ids: string[] }>) => {
      if (event.detail.ids?.includes(uniqueId)) {
        setIsDragging(true);
      }
    });

    useEvent(EVT_ELEMENT_DROP, (event: CustomEvent<{ ids: string[] }>) => {
      if (event.detail.ids?.includes(uniqueId)) {
        setIsDragging(false);
      }
    });

    // Listen for keyboard movement events
    useEffect(() => {
      if (!isOnlySelectedConnector) return;

      const handleKeyDown = (e: KeyboardEvent) => {
        if (document.activeElement !== document.body) return; // Only when canvas is focused
        if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(e.key)) {
          setIsKeyboardMoving(true);
        }
      };

      const handleKeyboardComplete = () => {
        setIsKeyboardMoving(false);
      };

      document.addEventListener("keydown", handleKeyDown);
      window.addEventListener("keyboard-movement-complete", handleKeyboardComplete);

      return () => {
        document.removeEventListener("keydown", handleKeyDown);
        window.removeEventListener("keyboard-movement-complete", handleKeyboardComplete);
      };
    }, [isOnlySelectedConnector]);

    // Functions

    const toStage = layer ? viewportToStage(layer.getAbsoluteTransform()) : noop;

    function viewportToElement(pos: Point) {
      const result = toStage(pos);
      const tr = getTransformParams("connector", element);
      return Transform.InvPoint(tr, result, result);
    }

    function dragAndSnap(index: number, point: Point, e: KonvaEventObject<MouseEvent>, cache?: WeakMap<any, any>) {
      let newDragState: utils.AnchorDragState, snappedShape: any;
      const otherIndex = 1 - index;

      const snap = attachUtils.checkSnapping(e.evt, e.target.getPosition(), e.target.getStage()!, layer!, cache); // TODO: can this return data in format of 'candidate' already?

      let candidate: any = null;
      if (snap) {
        if (snap instanceof attachUtils.SnapToAnchor) {
          const otherAnchor = utils.elementPointToCanvasPoint(
            element,
            (otherIndex == 0 ? p1 : p2) as ConnectorEndpoint
          );
          const { node, rect, distance } = snap;
          const insideShape = isPointInRect(otherAnchor, rect) && isPointInRect(e.target.getPosition(), rect);

          if (insideShape) {
            if (distance < connectorConsts.DistanceToSnapToAnchorFromInside_px) {
              candidate = {
                node,
                side: snap.side,
                position: snap.position,
                anchors: snap.anchors,
                rotation: snap.rotation,
              };
            }
          } else {
            const smallShape =
              rect.width * rect.height < defaultShapeDimensions.width * defaultShapeDimensions.height * 3;
            if (smallShape) {
              candidate = {
                node,
                side: snap.side,
                position: snap.position,
                anchors: snap.anchors,
                rotation: snap.rotation,
              };
            } else if (distance < connectorConsts.DistanceToSnapToAnchorFromOutside_px) {
              candidate = {
                node,
                side: snap.side,
                position: snap.position,
                anchors: snap.anchors,
                rotation: snap.rotation,
              };
            }
          }
        } else if (snap instanceof attachUtils.SnapToOutline) {
          candidate = {
            node: snap.node,
            side: "outline",
            position: snap.xy,
            rotation: snap.rotation || 0,
            t: snap.t,
            anchors: null,
          };
        } else if (snap instanceof attachUtils.SnapJustShowAnchors) {
          candidate = { anchors: snap.anchors };
        } else {
          console.warn("unknown ret value", snap);
        }
      }

      // Internal drag state
      newDragState = { index, x: point.x, y: point.y } as utils.AnchorDragState;

      snappedShape = { id: null, element: null };

      // don't allow connection to same place as other endpoint)
      if (candidate?.node) {
        if (
          candidate.node.id() == element.connectedShapes[otherIndex]?.id &&
          candidate.side != "outline" &&
          candidate.side == utils.fixAnchorsMode(element.anchorsMode?.[otherIndex])
        ) {
          candidate = null;
        }
      }

      if (candidate?.node) {
        // The attached shape update
        const attachedConnectors = structuredClone(candidate.node.attrs.element.attachedConnectors ?? {});
        attachedConnectors[uniqueId] = { lineId: uniqueId };
        snappedShape.id = candidate.node.id();
        snappedShape.element = { attachedConnectors };

        // update the drag state
        const pointInElementSpace = utils.canvasPointToElementPoint(element, { ...candidate.position }) as any;
        newDragState.snapToPoint = { ...pointInElementSpace, rotation: candidate.rotation };
        newDragState.snapToShape = candidate.node.id();
      }
      // element is readonly, we have to create copies
      // TODO: get patch and return that instead of the whole element
      const { points, connectedShapes, anchorsMode, point1_t, point2_t } = produce(element, (draft) => {
        draft.points[index] = point;
        draft.connectedShapes[index] = candidate?.node ? { id: candidate.node.id(), type: "" } : { id: "", type: "" };
        draft.anchorsMode ??= ["n/a", "n/a"];
        draft.anchorsMode[index] = candidate?.side ?? "n/a";
        if (candidate?.side == "outline") {
          index == 0 ? (draft.point1_t = candidate.t) : (draft.point2_t = candidate.t);
        }
      });
      const newConnectorState = structuredClone({ points, connectedShapes, anchorsMode, point1_t, point2_t });

      return {
        newDragState,
        newAnchorPoints: candidate?.anchors,
        newConnectorState,
        snappedShape,
        highlightedSocket: candidate?.position,
      } as const;
    }

    const onDragStart = (index: number, e: KonvaEventObject<MouseEvent>) => {
      setConnectorTransforming(true);
      const shape = getShape(index);
      const prevId = element.connectedShapes[index]?.id;

      if (Boolean(prevId) != Boolean(shape)) {
        // We're never supposed to get here. If we drew the connector then we surely
        // got the data about the shape, so we should have both of them.
        console.warn("Connector didn't get from replicache its connected shape");
      }

      // duplicate the shape we're connected to state for undo information
      const attachedConnectors = prevId && shape && { ...shape.attachedConnectors };

      const p = e.target.getPosition(); // position on screen of control point
      const { x, y } = viewportToElement(p); // position relative to element
      setDragState({
        anchorDragState: {
          index,
          x,
          y,
        },
      });
      anchorsFrameIds.current[index] = null; // mark us as detachd from frame
      undoState.current = {
        connector: structuredClone(element),
        shape: {
          id: prevId,
          element: attachedConnectors ? { attachedConnectors } : undefined,
        },
      };
      // TODO: this 2 calls to replicache can be unified into 1 call
      // detach from shape
      const newConnectorData = structuredClone(element);
      newConnectorData.connectedShapes[index] = { id: "", type: "" };
      changeElement(newConnectorData, { shouldAdd: false });
      // detach shape from us
      if (attachedConnectors && prevId) {
        delete attachedConnectors[uniqueId];
        changeAnyElement(prevId, { attachedConnectors });
      }
    };

    const onDragEnd = (anchorIndex: number, mousePosition: Point, e: KonvaEventObject<MouseEvent>) => {
      setConnectorTransforming(false);
      // This function changes the connector element in reflect, and registers an undo action.
      // There's a case we don't want to do that: if e.evt==null, because that means
      // the drag operation was cancelled because space was pressed.

      // if !e.evt - dragging was cancelled because space was pressed
      // if we should disable snapping, we don't do anything since the connector was moved already in dragmove
      if (e.evt) {
        const point = utils.canvasPointToElementPoint(element, mousePosition);
        const cache = e.target.attrs.cache;
        const { newConnectorState, snappedShape } = dragAndSnap(anchorIndex, point, e, cache);

        // TODO: don't apply the change if there's no difference between the new-state and undo-state
        // TODO: keep the whole element in undoState, and use immer to do the change.
        // then just check if patch.length!=0
        const prev = undoState.current;
        const cur = {
          connector: newConnectorState,
          shape: snappedShape,
        };
        const prevState = new Map();
        const newState = new Map();
        prevState.set(uniqueId, prev.connector);
        newState.set(uniqueId, cur.connector);
        if (prev.shape.id) {
          prevState.set(prev.shape.id, prev.shape.element);
          const node = layer!.findOne("." + prev.shape.id);
          if (node?.attrs.element) newState.set(prev.shape.id, node.attrs.element);
        }
        // if user doesn't want to snap, we continue to register the undo action,
        // just without snapping
        const disableSnapping = utils.shouldDisableConnectorSnapping(e.evt);
        if (!disableSnapping && cur.shape.id) {
          newState.set(cur.shape.id, cur.shape.element);
        }
        onElementsMutationEnded(newState, prevState);
      }
      delete e.target.attrs.cache;
      setDragState(null);
      undoState.current = null;
    };

    const onDragMove = (index: number, mousePosition: Point, e: KonvaEventObject<MouseEvent>) => {
      if (!e.evt) {
        // dragging was cancelled because space was pressed
        return;
      }

      // TODO: according to the profiler this function punches above its weight !
      // I can cache the transform for the connector, it almost certainly doesn't change when moving the endpoints
      let point = utils.canvasPointToElementPoint(element, mousePosition);

      // support snapping to 45 degrees on shift
      if (e.evt.shiftKey) {
        let otherAnchor = index == 1 ? (p1 as any) : (p2 as any);
        if (element.innerPoints?.length) {
          otherAnchor = element.innerPoints[index == 1 ? element.innerPoints.length - 1 : 0];
        }
        point = attachUtils.snapConnectorRotation(point, otherAnchor);
        // set the position of the actual anchor element
        e.target.setPosition(utils.elementPointToCanvasPoint(element, point));
      }

      if (utils.shouldDisableConnectorSnapping(e.evt)) {
        setDragState({ anchorDragState: { index, x: point.x, y: point.y } as utils.AnchorDragState });
        return;
      }
      const cache = (e.target.attrs.cache ??= new WeakMap<any, any>());
      let { newDragState, newConnectorState, newAnchorPoints, highlightedSocket } = dragAndSnap(index, point, e, cache);
      if (newDragState.snapToPoint) {
        const elementPos = newDragState.snapToPoint;
        const p = utils.elementPointToCanvasPoint(element, elementPos);
        e.target.setAttrs(p);
      }
      const anchorDragState = newDragState;
      if (newConnectorState) {
        changeElement(newConnectorState, { shouldAdd: false });
      } else {
        console.warn("no newConnectorState");
      }
      const shapeStandardAnchors = newAnchorPoints ? Object.values(newAnchorPoints) : null;
      if (!newDragState?.snapToPoint) {
        highlightedSocket = null;
      }
      setDragState({ anchorDragState, shapeSockets: shapeStandardAnchors, highlightedSocket });
    };

    const InvalidPoint = { x: Number.NaN, y: Number.NaN };

    function calcAnchorPosition(index: utils.AnchorIndex): { loaded: boolean; point: ConnectorEndpoint } {
      if (dragState?.anchorDragState?.index == index) {
        return { loaded: true, point: dragState?.anchorDragState.snapToPoint ?? dragState.anchorDragState };
      }
      if (!connectedShapes[index]?.id) {
        return { loaded: true, point: element.points[index] };
      }
      const shape = getShape(index);
      if (shape == connectorConsts.DATA_NOT_LOADED) {
        return { loaded: false, point: InvalidPoint };
      }
      if (shape == null || shape.hidden || shape.containerId) {
        return { loaded: true, point: InvalidPoint };
      }
      const mode = utils.fixAnchorsMode(element.anchorsMode?.[index]);
      if (mode != "n/a") {
        const elementType = getElementTypeForId(connectedShapes[index]!.id);
        // TODO: can delete this - since uvToXY repeats this code anyway
        let anchorPoints = anchorUtils.getAnchorPoints(elementType, shape);
        if (!anchorPoints) {
          return { loaded: true, point: InvalidPoint };
        }
        if (elementType == consts.CANVAS_ELEMENTS.FILE) {
          const width = anchorPoints.right.x - anchorPoints.left.x;
          const height = anchorPoints.buttom.y - anchorPoints.top.y;
          if ((width == 0 || height == 0) && layer) {
            const node = layer.findOne("#" + connectedShapes[index]!.id);
            if (!node) {
              return { loaded: true, point: InvalidPoint };
            }
            anchorPoints = anchorUtils.getAnchorPointsFromNode(node);
          }
        }
        if (!anchorPoints) {
          return { loaded: true, point: InvalidPoint };
        }
        const t = index == 0 ? element.point1_t : element.point2_t;
        let result: any = null;
        if (mode != "outline") {
          const anchors = anchorUtils.getAnchorPoints(elementType, shape);
          if (anchors) {
            result = anchors[mode as keyof typeof anchors];
            const r = utils.canvasPointToElementPoint(currentElement, result);
            result = { ...r, rotation: result.rotation };
          }
        } else {
          const rotation = normalAt(elementType, shape)(t);
          const xy = {
            ...utils.canvasPointToElementPoint(currentElement, pointAt(elementType, shape)(t)),
            rotation: rotation as Degrees,
          };
          result = xy;
        }
        return { loaded: true, point: result };
      }
      console.warn("connector attached to shape, but without connection side", element);
      return { loaded: true, point: InvalidPoint };
    }

    // Before we render we need to get the location of the shapes we're connected to.
    const { loaded: p1Loaded, point: p1 } = calcAnchorPosition(utils.AnchorIndex.Start);
    const { loaded: p2loaded, point: p2 } = calcAnchorPosition(utils.AnchorIndex.End);
    const loaded = p1Loaded && p2loaded;

    const startRotate = startShape?.rotate ?? 0;
    const endRotate = endShape?.rotate ?? 0;
    const startBbox = useDeepEqualMemo(
      utils.getConnectedShapeBbox(currentElement, p1, connectedShapes[0]?.id, startShape)
    );
    const endBbox = useDeepEqualMemo(utils.getConnectedShapeBbox(currentElement, p2, connectedShapes[1]?.id, endShape));
    const memoizedElement = useDeepEqualMemo({
      lineType: element.lineType,
      innerPoints: element.innerPoints,
      strokeWidth: parseStrokeWidth(element.strokeWidth),
      scaleX: element.scaleX,
      scaleY: element.scaleY,
      rotate: element.rotate,
      anchorsMode: element.anchorsMode,
      pointerStyles: element.pointerStyles,
    });

    const data = (() => {
      if (!loaded) return null;

      // Some old canvases didn't have lineType in some connectors :-O
      // I have no idea how it happened, but the element can't work without it!!
      if (!memoizedElement.lineType) return null;

      // We don't show our connector if it's attached to a deleted shape
      if (Number.isNaN(p1.x) || Number.isNaN(p2.x)) return null;

      return computeConnectorDrawingData(p1, p2, memoizedElement, startBbox, endBbox, startRotate, endRotate);
    })();

    //////////////////////////////////////////////////
    // Beyond this point - don't write hooks !!!!
    //////////////////////////////////////////////////

    // If we're connected to another shape we and don't have its info yet,
    // We don't render but instead we return an empty Group with our key,
    // Just so react won't unmount the component.
    // Note: this happens on initial load, after connecting, maybe other cases
    if (!loaded) {
      return (
        <Group
          key={uniqueId}
          isCanvasElement={false}
          isSelectable={isSelectable}
          isConnectable={false}
          isConnector={false}
          isFrame={false}
        />
      );
    }

    // If drawing data did not load, return null
    if (!data) return null;

    // During rendering we recheck the frames for both anchor.
    // It might seem enough to check this on drag-end of the anchors,
    // but the connected shapes might have moved and we need to check again.
    const startPoint = { ...utils.elementPointToCanvasPoint(element, p1), rotation: p1.rotation };
    const endPoint = { ...utils.elementPointToCanvasPoint(element, p2), rotation: p2.rotation };
    return (
      <>
        <Group
          id={uniqueId}
          name={uniqueId}
          key={uniqueId}
          type={consts.CANVAS_ELEMENTS.CONNECTOR}
          x={element.x}
          y={element.y}
          scaleX={element.scaleX}
          scaleY={element.scaleY}
          rotation={element.rotate}
          isSelectable={isSelectable}
          startpoint={p1}
          endpoint={p2}
          {...mutation.getCallbacks()}
          isCanvasElement={true}
          isConnectable={false}
          isConnector={true}
          isDraggable={true}
          isFrame={false}
          element={element}
        >
          {/* <OrthogonalConnectorDebugger
            byproducts={debugConnectorDraw(p1, p2, element, startBbox, endBbox, startRotate, endRotate).byproducts}
          /> */}
          <ConnectorLineAndText
            p1={p1 as any}
            p2={p2 as any}
            element={element}
            data={data}
            isEditing={isEditing}
            onChangeElement={changeElement}
          />
        </Group>
        <Portal enabled selector=".Overlay">
          {drawHandles && !isDragging && !isKeyboardMoving && (
            <>
              <AnchorPoints
                start={startPoint}
                end={endPoint}
                onDragStart={onDragStart}
                onDragMove={onDragMove}
                onDragEnd={onDragEnd}
              />
              <ConnectorEditor start={p1} end={p2} data={data} element={element} onChangeElement={changeElement} />
            </>
          )}
          {dragState?.shapeSockets && <ConnectionPoints anchorPoints={dragState.shapeSockets} />}
          {dragState?.highlightedSocket && (
            <SingleConnectionPoint point={dragState.highlightedSocket} highlight={true} />
          )}
        </Portal>
      </>
    );
  }
);

ConnectorCanvasElement.displayName = "ConnectorCanvasElement";

function SingleConnectionPoint({ point, highlight }: { point: Point; highlight: boolean }) {
  return (
    <Shape
      x={point.x}
      y={point.y}
      listening={false}
      fill={highlight ? ShapeSocketColor : "white"}
      stroke={highlight ? "white" : ShapeSocketColor}
      strokeWidth={2}
      strokeScaleEnabled={false}
      radius={connectorConsts.ConnectorSnapPointRadius}
      sceneFunc={drawScaledCircle}
    />
  );
}

function ConnectionPoints({ anchorPoints }: { anchorPoints: Point[] }) {
  return (
    <>
      {anchorPoints.map((p, index) => (
        <SingleConnectionPoint point={p} highlight={false} key={"anchor connection-point" + index} />
      ))}
    </>
  );
}

export function connectorTraits(element: Connector): ITraits {
  return {
    lineColor: element.stroke,
    connectorLineWidth: parseStrokeWidth(element.strokeWidth),
    dash: element.dash ?? 0,

    arrowHeads: element.pointerStyles,
    lineStyle: element.lineType,

    textColor: element.textColor ?? consts.DEFAULTS.TEXT_COLOR,
    fontProps: element.fontProps ?? 0,
    fontSize: getFontSize(element),
    font: element.font ?? consts.DEFAULTS.FONT,
  };
}

export function connectorValidateTrait(element: Connector, trait: Trait, value: any) {
  if (trait === Trait.lineStyle) {
    if (element.lineType === "elbow" || value === "elbow") {
      return { lineType: value, innerPoints: [] };
    }
    return { lineType: value };
  }
  if (trait == Trait.arrowHeads) {
    const pointerStyles = [...element.pointerStyles];
    if (value[0] !== "keep") pointerStyles[0] = value[0] ?? "none";
    if (value[1] !== "keep") pointerStyles[1] = value[1] ?? "none";
    return { pointerStyles };
  }
  return value;
}
